import React from 'react'
import { Box, Typography, Skeleton, useTheme, alpha } from '@mui/material'
import { ArrowDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const FunnelChart = ({ data, loading, title }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const goldColor = theme.palette.primary.main

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: '8px' }} />
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {t('analytics_no_funnel_data')}
        </Typography>
      </Box>
    )
  }

  const maxCount = Math.max(...data.map(s => s.count), 1)

  return (
    <Box>
      {data.map((step, idx) => {
        const widthPct = Math.max((step.count / maxCount) * 100, 8)
        const isLast = idx === data.length - 1

        return (
          <Box key={step.step}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
              <Box sx={{ width: 140, flexShrink: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    textAlign: 'right',
                  }}
                >
                  {step.step}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    height: 32,
                    width: `${widthPct}%`,
                    borderRadius: '6px',
                    background: `linear-gradient(90deg, ${alpha(goldColor, 0.15)}, ${alpha(goldColor, 0.3)})`,
                    border: `1px solid ${alpha(goldColor, 0.25)}`,
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: `linear-gradient(90deg, ${alpha(goldColor, 0.2)}, ${alpha(goldColor, 0.4)})`,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {step.count.toLocaleString()}
                  </Typography>
                </Box>
                {step.conversion_pct !== null && step.conversion_pct !== undefined && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      color: step.conversion_pct >= 50
                        ? theme.palette.success.main
                        : step.conversion_pct >= 20
                          ? theme.palette.warning.main
                          : theme.palette.error.main,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {step.conversion_pct}%
                  </Typography>
                )}
              </Box>
            </Box>

            {!isLast && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 0.5 }}>
                <Box sx={{ width: 140 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
                  <ArrowDown size={14} style={{ color: theme.palette.text.disabled }} />
                  {step.dropoff_pct !== null && step.dropoff_pct !== undefined && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.68rem',
                        color: theme.palette.error.main,
                        fontWeight: 500,
                      }}
                    >
                      {step.dropoff_pct}{t('analytics_drop_off')}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

export default FunnelChart
